// JobMap.js — Dispatch map for hub.solarability.com
// Reads and writes the Hub's existing `projects` table (same jobs the
// Schedule calendar and Projects tracker use), so nothing is duplicated.
// Pasted addresses become new projects; pins are the projects' lat/lng
// (columns added by JOBMAP_SQL.md). Leaflet loads from a CDN at runtime.
//
// Props (all passed from App.js): supabase, db, user, C, S, t, lang

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const OFFICE = { name: "SolarAbility office", lat: 41.6953, lng: -87.5355 }; // 10933 S Avenue O
const STATUSES = [
  { val: "not_started", en: "Not started", es: "Sin iniciar" },
  { val: "in_progress", en: "In progress", es: "En progreso" },
  { val: "on_hold", en: "On hold", es: "En espera" },
  { val: "completed", en: "Completed", es: "Completado" },
];

// ---- Leaflet loader (CDN) ----
function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    if (!document.getElementById("leaflet-css")) {
      const css = document.createElement("link");
      css.id = "leaflet-css"; css.rel = "stylesheet";
      css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(css);
    }
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => resolve(window.L); s.onerror = reject;
    document.head.appendChild(s);
  });
}

// "8148 S PAXTON AVE CHICAGO 60617 IL" -> "8148 S PAXTON AVE CHICAGO, IL 60617"
export function normalizeAddress(line) {
  let a = String(line || "").trim().replace(/\s+/g, " ");
  const m = a.match(/^(.*?)[,\s]+(\d{5})(?:-\d{4})?[,\s]+([A-Za-z]{2})$/);
  if (m) a = `${m[1]}, ${m[3].toUpperCase()} ${m[2]}`;
  if (a && !/\b(IL|Illinois|IN|Indiana|WI|Wisconsin|MI|Michigan)\b/i.test(a)) a += ", IL";
  return a;
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function geocode(address) {
  try { // OpenStreetMap Nominatim — free, 1 req/sec
    const r = await fetch("https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=" + encodeURIComponent(address), { headers: { "Accept-Language": "en" } });
    const j = await r.json();
    if (j && j[0]) return { lat: +j[0].lat, lng: +j[0].lon };
  } catch (e) {}
  try { // US Census fallback
    const r = await fetch("https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?benchmark=Public_AR_Current&format=json&address=" + encodeURIComponent(address));
    const j = await r.json();
    const m = j?.result?.addressMatches?.[0]?.coordinates;
    if (m) return { lat: +m.y, lng: +m.x };
  } catch (e) {}
  return null;
}

function distKm(a, b) {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function routeOrder(jobs) {
  const left = jobs.filter((j) => j.lat && j.lng), out = [];
  let cur = OFFICE;
  while (left.length) {
    let bi = 0, bd = Infinity;
    for (let i = 0; i < left.length; i++) {
      const d = distKm(cur, left[i]);
      if (d < bd) { bd = d; bi = i; }
    }
    const [n] = left.splice(bi, 1); out.push(n); cur = n;
  }
  return out;
}
function gmapsRouteUrl(ordered) {
  const pts = ordered.map((j) => `${j.lat},${j.lng}`);
  if (!pts.length) return "#";
  const way = pts.slice(0, -1).join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${OFFICE.lat},${OFFICE.lng}&destination=${pts[pts.length - 1]}` + (way ? `&waypoints=${encodeURIComponent(way)}` : "");
}

// A job is "done" for dispatch purposes once it's marked completed or its
// invoice has gone out (sent or paid) — those never need a crew again.
const invoiceOut = (p) => !!(p.invoice && (p.invoice.sentDate || p.invoice.paidDate));
const isDone = (p) => p.status === "completed" || invoiceOut(p);

const dayOf = (iso) => (iso ? String(iso).slice(0, 10) : "");
const timeOf = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d) ? "" : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
const localIso = (day, time) => { if (!day) return ""; const d = new Date(`${day}T${time || "08:00"}:00`); return isNaN(d) ? "" : d.toISOString(); };

// Textarea that saves when you tab away (not on every keystroke — each save
// writes the job row).
function ScopeBox({ value, onSave, inp, placeholder }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <textarea value={v} rows={2} placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== (value || "")) onSave(v); }}
      style={{ ...inp, marginTop: 4, minHeight: 48, resize: "vertical", fontFamily: "inherit" }} />
  );
}

export default function JobMap({ supabase, db, user, C, S, t, lang, focusId, onFocusHandled }) {
  const es = lang === "es";
  const L_ = (en, sp) => (es ? sp : en);
  const statusColor = { not_started: C.muted, in_progress: C.info, on_hold: C.warning, completed: C.success };

  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paste, setPaste] = useState("");
  const [pastePartner, setPastePartner] = useState("");
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [dayFilter, setDayFilter] = useState("all"); // all | unscheduled | YYYY-MM-DD
  const [showDone, setShowDone] = useState(false);
  const [showRoute, setShowRoute] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [picked, setPicked] = useState(() => new Set()); // compare mode: checked job ids
  const [bulkDay, setBulkDay] = useState("");
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const mapRef = useRef(null), mapObj = useRef(null), layerRef = useRef(null);

  // ---- Load ----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pj, pt] = await Promise.all([db.getProjects(), db.getPartners().catch(() => [])]);
      setProjects(pj || []);
      setPartners((pt || []).map((p) => p.name).filter(Boolean));
      if (supabase) {
        const { data } = await supabase.from("profiles").select("email, full_name");
        setProfiles(data || []);
      }
    } catch (e) { console.warn("JobMap load failed:", e); }
    setLoading(false);
  }, [db, supabase]);
  const loadedRef = useRef(false);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  // ---- Writes ----
  // Save through db.saveProject so the Projects tab, Schedule calendar and
  // local cache all stay in step; lat/lng go straight to the table because
  // saveProject doesn't carry them (and doesn't overwrite them either).
  const saveProject = useCallback(async (next) => {
    setProjects((ps) => ps.map((p) => (p.id === next.id ? next : p)));
    await db.saveProject(next);
  }, [db]);
  const savePin = useCallback(async (id, g) => {
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, lat: g.lat, lng: g.lng } : p)));
    if (supabase) { const { error } = await supabase.from("projects").update({ lat: g.lat, lng: g.lng }).eq("id", id); if (error) alert("Pin save failed: " + error.message); }
  }, [supabase]);

  const setSchedule = (p, day, time) => {
    const iso = localIso(day, time);
    const next = { ...p, scheduled_at: iso };
    const live = (p.visits || []).filter((v) => !v.cancelled);
    if (live.length) {
      const soon = [...live].sort((a, b) => new Date(a.scheduled_at || 0) - new Date(b.scheduled_at || 0))[0];
      next.visits = (p.visits || []).map((v) => (v.id === soon.id ? { ...v, scheduled_at: iso } : v));
    }
    if (iso && next.status === "not_started") next.status = "in_progress";
    saveProject(next);
  };
  const addMember = (p, email) => {
    if (!email || (p.members || []).some((m) => (m.name || "").toLowerCase() === email.toLowerCase())) return;
    const prof = profiles.find((x) => (x.email || "").toLowerCase() === email.toLowerCase());
    saveProject({ ...p, members: [...(p.members || []), { name: email, displayName: prof?.full_name || email, role: "Crew", addedAt: new Date().toISOString(), addedBy: user?.name }] });
  };
  const removeMember = (p, name) => saveProject({ ...p, members: (p.members || []).filter((m) => m.name !== name) });
  const addNote = (p) => {
    const text = noteText.trim(); if (!text) return;
    saveProject({ ...p, notes: [...(p.notes || []), { id: Date.now(), text, by: user?.name, at: new Date().toISOString() }] });
    setNoteText("");
  };

  async function handlePaste() {
    const lines = paste.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    setPaste("");
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const have = new Set(projects.map((p) => norm(p.address)));
    const fresh = lines.map(normalizeAddress).filter((a) => a && !have.has(norm(a)));
    if (!fresh.length) { setBusy(L_("Nothing new — those addresses are already jobs.", "Nada nuevo — esas direcciones ya son trabajos.")); setTimeout(() => setBusy(""), 3000); return; }
    for (let i = 0; i < fresh.length; i++) {
      setBusy(`${L_("Locating", "Ubicando")} ${i + 1} / ${fresh.length}…`);
      const g = await geocode(fresh[i]);
      const p = {
        id: `proj-${Date.now()}-${i}`, name: fresh[i].split(",")[0], client: "", address: fresh[i],
        systemSize: "", status: "not_started", completion: 0, tasks: [], materials: [], notes: [], members: [],
        projectType: "service", serviceSubtype: "", partner: pastePartner || "", playbookId: "", referenceIds: [],
        truckList: "", scheduled_at: "", visits: [], created_at: new Date().toISOString(), created_by: user?.name,
        lat: g?.lat ?? null, lng: g?.lng ?? null,
      };
      await db.saveProject(p);
      if (g) await savePin(p.id, g);
      setProjects((ps) => [p, ...ps]);
      await sleep(1100);
    }
    setBusy("");
  }

  async function locateMissing() {
    const missing = projects.filter((p) => p.address && !(p.lat && p.lng) && !isDone(p));
    for (let i = 0; i < missing.length; i++) {
      setBusy(`${L_("Locating", "Ubicando")} ${i + 1} / ${missing.length}…`);
      const g = await geocode(normalizeAddress(missing[i].address));
      if (g) await savePin(missing[i].id, g);
      await sleep(1100);
    }
    setBusy("");
  }
  async function relocate(p) {
    setBusy(L_("Locating…", "Ubicando…"));
    const g = await geocode(normalizeAddress(p.address));
    if (g) await savePin(p.id, g); else alert(L_("Couldn't locate that address — edit it in Projects and try again.", "No se pudo ubicar — edítala en Proyectos e intenta de nuevo."));
    setBusy("");
  }

  // ---- Filters ----
  const days = useMemo(() => [...new Set(projects.map((p) => dayOf(p.scheduled_at)).filter(Boolean))].sort(), [projects]);
  const visible = useMemo(() => projects.filter((p) => {
    if (!p.address) return false;
    if (!showDone && isDone(p)) return false;
    if (dayFilter === "unscheduled") return !p.scheduled_at;
    if (dayFilter !== "all") return dayOf(p.scheduled_at) === dayFilter;
    return true;
  }), [projects, dayFilter, showDone]);
  // Compare mode: when jobs are checked, the map shows only those, always in
  // suggested drive order, with the leg distance on each pin.
  const compare = picked.size > 0;
  const base = useMemo(() => (compare ? projects.filter((p) => picked.has(p.id) && p.address) : visible), [compare, projects, picked, visible]);
  const routed = useMemo(() => ((compare || (showRoute && dayFilter !== "all" && dayFilter !== "unscheduled")) ? routeOrder(base) : null), [compare, showRoute, base, dayFilter]);
  const routeIndex = useMemo(() => { const m = {}; (routed || []).forEach((j, i) => (m[j.id] = i + 1)); return m; }, [routed]);
  const legs = useMemo(() => { // miles from previous stop (office first)
    const m = {}; let prev = OFFICE, total = 0;
    (routed || []).forEach((j) => { const mi = distKm(prev, j) * 0.621371; m[j.id] = mi; total += mi; prev = j; });
    return { m, total, back: routed && routed.length ? distKm(prev, OFFICE) * 0.621371 : 0 };
  }, [routed]);
  const mapJobs = routed || base;
  // The list keeps showing every job that matches the filters (so more can be
  // checked); checked ones float to the top in drive order.
  const listJobs = useMemo(() => (compare ? [...(routed || []), ...visible.filter((p) => !picked.has(p.id))] : (routed || base)), [compare, routed, visible, picked, base]);
  const sel = projects.find((p) => p.id === selected) || null;

  // Opened from a project's "Open in Job Map" button: select it, widen the
  // filters so it's visible, and geocode it if it has no pin yet.
  const focusRef = useRef(null);
  useEffect(() => {
    if (loading || !focusId || focusRef.current === focusId) return;
    const p = projects.find((x) => x.id === focusId);
    if (!p) return;
    focusRef.current = focusId;
    setDayFilter("all"); setShowRoute(false);
    if (isDone(p)) setShowDone(true);
    setSelected(p.id);
    if (p.address && !(p.lat && p.lng)) relocate(p);
    if (onFocusHandled) onFocusHandled();
    // eslint-disable-next-line
  }, [loading, focusId, projects]);

  // ---- Map ----
  useEffect(() => {
    let dead = false;
    loadLeaflet().then((L) => {
      if (dead || mapObj.current || !mapRef.current) return;
      const m = L.map(mapRef.current).setView([41.72, -87.75], 10);
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap contributors", maxZoom: 19, className: "jobmap-tiles" }).addTo(m);
      L.circleMarker([OFFICE.lat, OFFICE.lng], { radius: 7, color: C.bg, fillColor: C.gold, fillOpacity: 1 }).bindTooltip(OFFICE.name).addTo(m);
      layerRef.current = L.layerGroup().addTo(m);
      mapObj.current = m;
      setTimeout(() => m.invalidateSize(), 50);
    }).catch(() => {});
    return () => { dead = true; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const L = window.L, m = mapObj.current, layer = layerRef.current;
    if (!L || !m || !layer) return;
    layer.clearLayers();
    const pts = [];
    mapJobs.forEach((j) => {
      if (!j.lat || !j.lng) return;
      pts.push([j.lat, j.lng]);
      const n = routeIndex[j.id], color = statusColor[j.status] || C.muted;
      const icon = L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 13],
        html: `<div style="background:${color};color:${C.bg};border:2px solid ${C.light};border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font:800 12px sans-serif;box-shadow:0 1px 4px rgba(0,0,0,.6);${selected === j.id ? `outline:3px solid ${C.gold};` : ""}">${n || "•"}</div>` });
      const crew = (j.members || []).map((x) => x.displayName || x.name).join(", ");
      L.marker([j.lat, j.lng], { icon })
        .bindTooltip(`${j.address}${j.scope ? `<br/><i>${String(j.scope).replace(/</g, "&lt;").slice(0, 120)}${j.scope.length > 120 ? "…" : ""}</i>` : ""}<br/>${j.partner ? j.partner + " · " : ""}${dayOf(j.scheduled_at) || L_("unscheduled", "sin fecha")}${crew ? "<br/>" + crew : ""}${n ? `<br/><b>${legs.m[j.id].toFixed(1)} mi</b> ${L_("from previous stop", "desde la parada anterior")}` : ""}`)
        .on("click", () => setSelected(j.id)).addTo(layer);
    });
    if (routed && routed.length) L.polyline([[OFFICE.lat, OFFICE.lng], ...routed.map((j) => [j.lat, j.lng])], { color: C.gold, weight: 3, dashArray: "6 6", opacity: 0.8 }).addTo(layer);
    if (pts.length && !selected) m.fitBounds(pts, { padding: [30, 30], maxZoom: 13 });
    // eslint-disable-next-line
  }, [mapJobs, selected, routed, legs]);

  useEffect(() => { if (sel?.lat && mapObj.current) mapObj.current.panTo([sel.lat, sel.lng]); }, [selected]); // eslint-disable-line

  // ---- UI ----
  const small = { fontSize: 12, color: C.muted };
  const card = { ...S.card, padding: 14, marginBottom: 0 };
  const inp = { ...S.input, padding: "8px 10px", fontSize: 13 };
  const selS = { ...S.select, padding: "8px 10px", fontSize: 13, width: "auto" };
  const btnP = { ...S.btnP, padding: "8px 14px", fontSize: 13 };
  const btnS = { ...S.btnS, padding: "8px 14px", fontSize: 13 };
  const unlocated = projects.filter((p) => p.address && !(p.lat && p.lng) && !isDone(p)).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{L_("Job Map", "Mapa de Trabajos")}</h2>
        <div style={small}>{mapJobs.length} {L_("jobs shown", "trabajos")}{unlocated ? ` · ${unlocated} ${L_("not located", "sin ubicar")}` : ""}</div>
      </div>

      <div className="jobmap-grid" style={{ display: "grid", gridTemplateColumns: "minmax(320px, 430px) 1fr", gap: 16, height: "calc(100vh - 150px)", minHeight: 560 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, overflow: "hidden" }}>
          {/* Add jobs */}
          <div style={card}>
            <div style={S.label}>{L_("Add jobs from a partner list", "Agregar trabajos de una lista")}</div>
            <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} style={{ ...S.textarea, minHeight: 0, padding: "8px 10px", fontSize: 13, marginBottom: 8 }}
              placeholder={L_("One address per line, e.g.\n8148 S PAXTON AVE CHICAGO 60617 IL", "Una dirección por línea, p. ej.\n8148 S PAXTON AVE CHICAGO 60617 IL")} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={pastePartner} onChange={(e) => setPastePartner(e.target.value)} style={selS}>
                <option value="">{L_("Partner…", "Socio…")}</option>
                {partners.map((p) => <option key={p}>{p}</option>)}
              </select>
              <button style={btnP} onClick={handlePaste} disabled={!!busy || !paste.trim()}>{L_("Add & pin", "Agregar y ubicar")}</button>
              {unlocated > 0 && <button style={btnS} onClick={locateMissing} disabled={!!busy}>{L_("Locate", "Ubicar")} {unlocated}</button>}
            </div>
            {busy && <div style={{ ...small, marginTop: 6, color: C.gold }}>{busy}</div>}
          </div>

          {/* Filters */}
          <div style={card}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={dayFilter} onChange={(e) => { setDayFilter(e.target.value); setShowRoute(false); setSelected(null); }} style={selS}>
                <option value="all">{L_("All days", "Todos los días")}</option>
                <option value="unscheduled">{L_("Unscheduled", "Sin fecha")}</option>
                {days.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <label style={{ ...small, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> {L_("Show completed / invoiced", "Ver completados / facturados")}
              </label>
              {dayFilter !== "all" && dayFilter !== "unscheduled" && (
                <button style={showRoute ? btnP : btnS} onClick={() => { setShowRoute((v) => !v); setSelected(null); }}>{showRoute ? L_("Route on", "Ruta activa") : L_("Suggest route", "Sugerir ruta")}</button>
              )}
              {routed && routed.length > 0 && <a href={gmapsRouteUrl(routed)} target="_blank" rel="noreferrer" style={{ ...btnS, textDecoration: "none" }}>Google Maps</a>}
            </div>
          </div>

          {/* Compare bar */}
          {compare && (
            <div style={{ ...card, border: `1px solid ${C.gold}66` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>
                  {picked.size} {L_("selected", "seleccionados")} · {legs.total.toFixed(0)} mi {L_("from office", "desde la oficina")}{routed && routed.length > 1 ? ` · ${(legs.total + legs.back).toFixed(0)} mi ${L_("round trip", "ida y vuelta")}` : ""}
                </div>
                <button style={{ ...btnS, padding: "4px 10px" }} onClick={() => setPicked(new Set())}>{L_("Clear", "Limpiar")}</button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
                <input type="date" value={bulkDay} onChange={(e) => setBulkDay(e.target.value)} style={{ ...inp, width: "auto" }} />
                <button style={btnP} disabled={!bulkDay} onClick={() => { (routed || []).forEach((p) => setSchedule(p, bulkDay, timeOf(p.scheduled_at))); setBulkDay(""); }}>{L_("Schedule all on this day", "Programar todos ese día")}</button>
                {routed && routed.length > 0 && <a href={gmapsRouteUrl(routed)} target="_blank" rel="noreferrer" style={{ ...btnS, textDecoration: "none" }}>Google Maps</a>}
              </div>
              <div style={{ ...small, marginTop: 6 }}>{L_("Map shows only the checked jobs, numbered in suggested drive order.", "El mapa muestra solo los trabajos marcados, numerados en orden de ruta sugerido.")}</div>
            </div>
          )}

          {/* Job list */}
          <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            {loading && <div style={small}>{L_("Loading…", "Cargando…")}</div>}
            {!loading && !listJobs.length && <div style={small}>{L_("No jobs match. Paste addresses above to get started.", "Sin trabajos. Pega direcciones arriba para empezar.")}</div>}
            {listJobs.map((p) => {
              const isSel = selected === p.id, color = statusColor[p.status] || C.muted;
              const crew = (p.members || []);
              return (
                <div key={p.id} onClick={() => setSelected(isSel ? null : p.id)} ref={isSel ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined}
                  style={{ ...card, padding: 12, cursor: "pointer", border: `1px solid ${isSel ? C.gold : C.border}`, boxShadow: isSel ? `0 0 0 1px ${C.gold}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.light, display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} onClick={(e) => e.stopPropagation()} title={L_("Select to compare", "Seleccionar para comparar")} style={{ marginTop: 2, accentColor: C.gold, flexShrink: 0 }} />
                      <span>
                      {routeIndex[p.id] && <span style={{ marginRight: 6, color: C.gold }}>#{routeIndex[p.id]}</span>}
                      <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 5, background: color, marginRight: 6 }} />
                      {p.address}
                      </span>
                    </div>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {invoiceOut(p) && <span style={S.badge(p.invoice.paidDate ? C.success : C.info)}>{p.invoice.paidDate ? L_("PAID", "PAGADO") : L_("INVOICED", "FACTURADO")}</span>}
                      {!(p.lat && p.lng) && <span title={L_("Not located", "Sin ubicar")} style={{ color: C.warning, fontSize: 12 }}>⚠</span>}
                    </span>
                  </div>
                  <div style={{ ...small, marginTop: 3, marginLeft: 24 }}>
                    {p.partner || "—"} · {dayOf(p.scheduled_at) ? `${dayOf(p.scheduled_at)} ${timeOf(p.scheduled_at)}` : L_("unscheduled", "sin fecha")}{crew.length ? " · " + crew.map((m) => m.displayName || m.name.split("@")[0]).join(", ") : ""}{routeIndex[p.id] ? ` · ${legs.m[p.id].toFixed(1)} mi` : ""}
                  </div>
                  {p.scope && !isSel && (
                    <div style={{ ...small, color: C.light, marginTop: 3, marginLeft: 24, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>🛠 {p.scope}</div>
                  )}

                  {isSel && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <label style={{ ...small, gridColumn: "1 / -1" }}>{L_("Scope of work", "Alcance del trabajo")}
                        <ScopeBox value={p.scope || ""} onSave={(v) => saveProject({ ...p, scope: v })} inp={inp} placeholder={L_("What we're doing here, in a line or two", "Qué se hace aquí, en una o dos líneas")} />
                      </label>
                      <label style={small}>{L_("Date", "Fecha")}<input type="date" value={dayOf(p.scheduled_at)} onChange={(e) => setSchedule(p, e.target.value, timeOf(p.scheduled_at))} style={{ ...inp, marginTop: 4 }} /></label>
                      <label style={small}>{L_("Time", "Hora")}<input type="time" value={timeOf(p.scheduled_at)} onChange={(e) => setSchedule(p, dayOf(p.scheduled_at), e.target.value)} style={{ ...inp, marginTop: 4 }} disabled={!dayOf(p.scheduled_at)} /></label>
                      <label style={small}>{L_("Status", "Estado")}
                        <select value={p.status || "not_started"} onChange={(e) => saveProject({ ...p, status: e.target.value })} style={{ ...S.select, padding: "8px 10px", fontSize: 13, marginTop: 4 }}>
                          {STATUSES.map((s) => <option key={s.val} value={s.val}>{es ? s.es : s.en}</option>)}
                        </select>
                      </label>
                      <label style={small}>{L_("Partner", "Socio")}
                        <select value={p.partner || ""} onChange={(e) => saveProject({ ...p, partner: e.target.value })} style={{ ...S.select, padding: "8px 10px", fontSize: 13, marginTop: 4 }}>
                          <option value="">—</option>
                          {partners.map((x) => <option key={x}>{x}</option>)}
                          {p.partner && !partners.includes(p.partner) && <option>{p.partner}</option>}
                        </select>
                      </label>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={small}>{L_("Crew", "Cuadrilla")}</div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "4px 0 6px" }}>
                          {crew.map((m) => (
                            <span key={m.name} style={{ ...S.badge(C.gold), display: "inline-flex", gap: 6, alignItems: "center" }}>
                              {m.displayName || m.name}
                              <span onClick={() => removeMember(p, m.name)} style={{ cursor: "pointer", opacity: 0.7 }}>✕</span>
                            </span>
                          ))}
                        </div>
                        <select value="" onChange={(e) => addMember(p, e.target.value)} style={{ ...S.select, padding: "8px 10px", fontSize: 13 }}>
                          <option value="">{L_("+ Add crew member", "+ Agregar")}</option>
                          {profiles.filter((x) => x.email && !crew.some((m) => (m.name || "").toLowerCase() === x.email.toLowerCase())).map((x) => <option key={x.email} value={x.email}>{x.full_name || x.email}</option>)}
                        </select>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={small}>{L_("Notes", "Notas")}</div>
                        {(p.notes || []).slice(-3).map((n) => <div key={n.id} style={{ ...small, color: C.light, marginTop: 3 }}>• {n.text} <span style={{ opacity: 0.6 }}>— {n.by}</span></div>)}
                        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                          <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote(p)} placeholder={L_("Add a note…", "Agregar nota…")} style={inp} />
                          <button style={btnS} onClick={() => addNote(p)}>+</button>
                        </div>
                      </div>
                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`} target="_blank" rel="noreferrer" style={{ ...btnS, textDecoration: "none" }}>{L_("Directions", "Cómo llegar")}</a>
                        <button style={btnS} onClick={() => relocate(p)} disabled={!!busy}>{L_("Re-locate pin", "Reubicar")}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div ref={mapRef} style={{ width: "100%", height: "100%", borderRadius: 16, border: `1px solid ${C.border}`, background: C.card }} />
      </div>
      <style>{`.jobmap-tiles { filter: invert(1) hue-rotate(180deg) brightness(0.85) contrast(0.9) saturate(0.6); } .leaflet-container { background: #0a1a12; }
        @media (max-width: 900px) { .jobmap-grid { grid-template-columns: 1fr !important; height: auto !important; } .jobmap-grid > div:last-child { height: 50vh !important; min-height: 320px; } }`}</style>
    </div>
  );
}
